<template>
    <main class="flex-1 bg-gray-50 dark:bg-white/3">
        <div class="py-6">
            <div class="max-w-(--breakpoint-2xl) mx-auto">
                <PageBreadcrumb page-title="Kategórie článkov" />
            </div>
            <TableCard title="Kategórie článkov">
                <template #controls>
                    <button
                        type="button"
                        class="shrink-0 px-4 py-2 bg-brand-600 text-white rounded-md"
                        @click="showAddModal = true"
                    >
                        Pridať kategóriu
                    </button>
                </template>
                <div class="max-w-full overflow-x-auto min-w-0">
                    <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                        <TableHeader
                            class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
                        >
                            <TableRow>
                                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                    ID
                                </TableCell>
                                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Názov
                                </TableCell>
                                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Popis
                                </TableCell>
                                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Počet článkov
                                </TableCell>
                                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Akcie
                                </TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                            <TableEmptyState
                                v-if="categories.length === 0"
                                title="Žiadne kategórie neboli nájdené"
                                hint="Pridajte prvú kategóriu."
                                colspan="5"
                            />
                            <TableRow v-for="category in categories" :key="category.id" class="hover:bg-gray-50 dark:hover:bg-white/5">
                                <TableCell class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white/90">
                                    {{ category.id }}
                                </TableCell>
                                <TableCell class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white/90">{{ category.name }}</div>
                                </TableCell>
                                <TableCell class="px-6 py-4">
                                    <div class="text-sm text-gray-500 max-w-xs truncate dark:text-gray-400">{{ category.description }}
                                    </div>
                                </TableCell>
                                <TableCell class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {{ category.posts_count || 0 }}
                                </TableCell>
                                <TableCell class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button @click="editCategory(category)"
                                        class="text-brand-600 hover:text-brand-900 mr-4">
                                        Upraviť
                                    </button>
                                    <button @click="deleteCategory(category)"
                                        class="text-red-600 hover:text-red-900"
                                        :disabled="(category.posts_count ?? 0) > 0">
                                        Zmazať
                                    </button>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </TableCard>
        </div>

        <!-- Add/Edit Modal -->
        <div v-if="showAddModal || showEditModal"
            class="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50 dark:bg-gray-900">
            <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-white/3">
                <div class="mt-3">
                    <h3 class="text-lg font-medium text-gray-900 mb-4 dark:text-white/90">
                        {{ showEditModal ? 'Upraviť kategóriu' : 'Pridať novú kategóriu' }}
                    </h3>

                    <form @submit.prevent="saveCategory" class="space-y-4">
                        <div>
                            <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Názov kategórie <span class="text-red-500">*</span>
                            </label>
                            <FormKit type="text" id="name" v-model="currentCategory.name" name="name"
                                :classes="{ input: 'mt-1 block w-full border-gray-300 rounded-md shadow-xs focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700' }"
                                placeholder="Zadajte názov kategórie" validation="required" />
                        </div>

                        <div>
                            <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Popis
                            </label>
                            <FormKit type="textarea" id="description" v-model="currentCategory.description" name="description" rows="3"
                                :classes="{ input: 'mt-1 block w-full border-gray-300 rounded-md shadow-xs focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700' }"
                                placeholder="Zadajte popis kategórie" />
                        </div>

                        <div class="flex justify-end space-x-3 pt-4">
                            <button type="button" @click="closeModal"
                                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-white/5">
                                Zrušiť
                            </button>
                            <button type="submit" :disabled="isSubmitting"
                                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-brand-600 border border-transparent rounded-md shadow-xs hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 size-4 text-white" fill="none"
                                    viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                                {{ isSubmitting ? 'Ukladám...' : (showEditModal ? 'Uložiť' : 'Pridať') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PostCategoryDataService from '../services/PostCategoryDataService'
import type { PostCategory } from '../services/PostCategoryDataService'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableEmptyState } from '@/components/common/table'

export default defineComponent({
    name: 'post-category-list',

    components: {
        Table,
        TableHeader,
        TableBody,
        TableRow,
        TableCell,
        TableCard,
        TableEmptyState,
        PageBreadcrumb,
    },

    data() {
        return {
            categories: [] as PostCategory[],
            showAddModal: false,
            showEditModal: false,
            isSubmitting: false,
            currentCategory: {
                name: '',
                description: ''
            } as PostCategory
        }
    },

    async mounted() {
        await this.loadCategories()
    },

    methods: {
        async loadCategories() {
            try {
                const response = await PostCategoryDataService.getAll()
                this.categories = (response.data as any)?.data || response.data
            } catch (error: unknown) {
                console.error('Error loading categories:', error)
                const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
                toast.error('Chyba pri načítavaní kategórií: ' + errorMessage)
            }
        },

        editCategory(category: PostCategory) {
            this.currentCategory = { ...category }
            this.showEditModal = true
        },
        async saveCategory() {
            if (!this.currentCategory.name?.trim()) {
                toast.error('Názov kategórie je povinný')
                return
            }

            this.isSubmitting = true

            try {
                if (this.showEditModal && this.currentCategory.id) {
                    await PostCategoryDataService.update(this.currentCategory.id, this.currentCategory)
                    toast.success('Kategória bola úspešne upravená!')
                } else {
                    await PostCategoryDataService.add(this.currentCategory)
                    toast.success('Kategória bola úspešne pridaná!')
                }

                await this.loadCategories()
                this.closeModal()
            } catch (error: unknown) {
                console.error('Error saving category:', error)
                const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
                toast.error('Chyba pri ukladaní kategórie: ' + errorMessage)
            } finally {
                this.isSubmitting = false
            }
        },

        async deleteCategory(category: PostCategory) {
            if (category.posts_count && category.posts_count > 0) {
                toast.error('Nemôžete zmazať kategóriu, ktorá obsahuje články')
                return
            }

            if (!confirm(`Naozaj chcete zmazať kategóriu "${category.name}"?`)) {
                return
            } try {
                if (category.id) {
                    await PostCategoryDataService.delete(category.id)
                }
                toast.success('Kategória bola úspešne zmazaná!')
                await this.loadCategories()
            } catch (error: unknown) {
                console.error('Error deleting category:', error)
                const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
                toast.error('Chyba pri mazaní kategórie: ' + errorMessage)
            }
        },

        closeModal() {
            this.showAddModal = false
            this.showEditModal = false
            this.currentCategory = {
                name: '',
                description: ''
            }
        }
    }
})
</script>
